/* "Custom Audience" is the product name for what proxima-audiences calls an audience definition.
   This module keeps the service's vocabulary: AudienceDefinition, definitionId, /audience-definitions. */
import type {
  AdAccountsApi,
  AudienceDefinitionsApi,
  AudienceDeliveriesApi,
  AudienceDestinationsApi,
  AudienceSnapshotsApi,
  FbIntegrationApi,
  CreateAudienceRequest as SdkCreateAudienceRequest,
  PreviewCountRequest as SdkPreviewCountRequest,
  UpdateAudienceRequest as SdkUpdateAudienceRequest
} from '@innovationdepartment/proxima-sdk-axios';
import type { AxiosResponse } from 'axios';
import { useAdAccountStore } from 'stores';
import type { AudienceDefinitionPreviewResponse } from '../types';
import type { CustomAudiencesApi } from './customAudiencesApi.types';
import { sendCustomAudiencesRequest } from './request';

/** The SDK classes the live api calls: proxima-audiences, plus proxima-integrations for the Meta terms re-check. */
export type CustomAudiencesSdk = {
  definitions: AudienceDefinitionsApi;
  snapshots: AudienceSnapshotsApi;
  destinations: AudienceDestinationsApi;
  deliveries: AudienceDeliveriesApi;
  adAccounts: AdAccountsApi;
  fbIntegration: FbIntegrationApi;
};

const DEFAULT_PAGE = { pageSize: 25, pageNumber: 1 };

export const ADD_DESTINATIONS_UNAVAILABLE_MESSAGE =
  'proxima-audiences has no endpoint to add destinations to a saved audience.';

/**
 * The live api, on the proxima-audiences SDK classes that `getSdk` returns at call time.
 *
 * Every call goes through `sendCustomAudiencesRequest`.
 * Request bodies are the adapters in `types/audiences`, which the service accepts though api.yml types them narrower.
 * Each is cast to its SDK type here, and nowhere else.
 */
export const createLiveCustomAudiencesApi = (
  getSdk: () => CustomAudiencesSdk
): CustomAudiencesApi => ({
  listDefinitions: (ctx, params = {}) =>
    sendCustomAudiencesRequest(ctx, 'GET', options =>
      getSdk().definitions.listAudiences(
        { brandId: ctx.brandId, ...DEFAULT_PAGE, ...params },
        options
      )
    ),
  getDefinition: (ctx, definitionId) =>
    sendCustomAudiencesRequest(ctx, 'GET', options =>
      getSdk().definitions.getAudience({ brandId: ctx.brandId, definitionId }, options)
    ),
  updateDefinition: (ctx, definitionId, body) =>
    sendCustomAudiencesRequest(ctx, 'PATCH', options =>
      getSdk().definitions.updateAudience(
        {
          brandId: ctx.brandId,
          definitionId,
          updateAudienceRequest: body as SdkUpdateAudienceRequest
        },
        options
      )
    ),
  getDeletePreview: (ctx, definitionId) =>
    sendCustomAudiencesRequest(ctx, 'GET', options =>
      getSdk().definitions.getAudienceDeletePreview({ brandId: ctx.brandId, definitionId }, options)
    ),
  deleteDefinition: (ctx, definitionId) =>
    sendCustomAudiencesRequest(ctx, 'DELETE', options =>
      getSdk().definitions.deleteAudience({ brandId: ctx.brandId, definitionId }, options)
    ),
  previewDefinition: (ctx, body) =>
    sendCustomAudiencesRequest(
      ctx,
      'POST',
      options =>
        /* The SDK's flat preview shape, narrowed to the union on `outcome` */
        getSdk().definitions.previewAudienceDefinition(
          { brandId: ctx.brandId, previewAudienceRequest: body },
          options
        ) as Promise<AxiosResponse<AudienceDefinitionPreviewResponse>>
    ),
  countPreview: (ctx, body) =>
    sendCustomAudiencesRequest(ctx, 'POST', options =>
      getSdk().definitions.countAudienceDefinitionPreview(
        { brandId: ctx.brandId, previewCountRequest: body as SdkPreviewCountRequest },
        options
      )
    ),
  createDefinition: (ctx, body) =>
    sendCustomAudiencesRequest(ctx, 'POST', options =>
      getSdk().definitions.createAudience(
        { brandId: ctx.brandId, createAudienceRequest: body as SdkCreateAudienceRequest },
        options
      )
    ),
  listSnapshots: (ctx, params) =>
    sendCustomAudiencesRequest(ctx, 'GET', options =>
      getSdk().snapshots.listAudienceSnapshots(
        { brandId: ctx.brandId, ...DEFAULT_PAGE, ...params },
        options
      )
    ),
  listDestinations: (ctx, { definitionId }) =>
    sendCustomAudiencesRequest(ctx, 'GET', options =>
      getSdk().destinations.listAudienceDestinations(
        { brandId: ctx.brandId, definitionId },
        options
      )
    ),
  listDeliveries: (ctx, { snapshotId }) =>
    sendCustomAudiencesRequest(ctx, 'GET', options =>
      getSdk().deliveries.listAudienceDeliveries({ brandId: ctx.brandId, snapshotId }, options)
    ),
  addDestinations: async () => ({
    error: { status: null, message: ADD_DESTINATIONS_UNAVAILABLE_MESSAGE }
  }),
  listBrandAdAccounts: async () => {
    const { metaAdAccountsList, tiktokAdAccountsList } = useAdAccountStore.getState();
    return { data: { metaAdAccountsList, tiktokAdAccountsList } };
  },
  listProximaAdAccounts: ctx =>
    sendCustomAudiencesRequest(ctx, 'GET', options =>
      getSdk().adAccounts.listProximaAdAccounts({ brandId: ctx.brandId }, options)
    ),
  checkAdAccountTos: async (ctx, adAccountId) => {
    const result = await sendCustomAudiencesRequest(
      ctx,
      'POST',
      options =>
        getSdk().fbIntegration.checkAdAccountTos({ brandId: ctx.brandId, adAccountId }, options),
      { retry: true }
    );
    if (result.error) return result;
    const acceptedTos = result.data.acceptedTos === true;
    if (acceptedTos) markBrandAdAccountTosAccepted(adAccountId);
    return { data: { acceptedTos } };
  }
});

/* Brand accounts are read from nova's ad account store, so an acceptance is written there for the next read */
const markBrandAdAccountTosAccepted = (adAccountId: string): void => {
  const { metaAdAccountsList } = useAdAccountStore.getState();
  const stale = metaAdAccountsList.some(
    account => account.adAccountId === adAccountId && !account.properties?.acceptedTos
  );
  if (!stale) return;
  useAdAccountStore.setState({
    metaAdAccountsList: metaAdAccountsList.map(account =>
      account.adAccountId === adAccountId
        ? {
            ...account,
            properties: { currency: account.properties?.currency ?? '', acceptedTos: true }
          }
        : account
    )
  });
};
